import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * A microphone icon that changes color based on speaking state.
 */
const MicrophoneIcon = ({ isSpeaking }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: isSpeaking ? '#4CAF50' : '#9E9E9E', transition: 'color 0.3s ease' }}
  >
    <path
      d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
      fill="currentColor"
    />
    <path
      d="M19 10v2a7 7 0 0 1-14 0v-2H3v2a9 9 0 0 0 8 8.94V23h2v-2.06A9 9 0 0 0 21 12v-2h-2z"
      fill="currentColor"
    />
  </svg>
);

/**
 * A React component that visualizes voice input with a microphone icon and a frequency graph.
 */
const VoiceVisualizer = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const canvasRef = useRef(null);

  // Use refs for audio components and animation loop to prevent re-renders and for cleanup.
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const animationFrameIdRef = useRef(null);

  const draw = useCallback(() => {
    if (!analyserRef.current || !canvasRef.current) return;

    const analyser = analyserRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    analyser.getByteFrequencyData(dataArray);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const barCount = 32; // Number of bars to display in the small canvas
    const barWidth = canvas.width / barCount;
    let overallVolume = 0;

    for (let i = 0; i < barCount; i++) {
      // Use a non-linear mapping to get a better visual spread of frequencies
      const dataIndex = Math.floor(Math.pow(i / barCount, 2) * (analyser.frequencyBinCount * 0.4));
      const barHeight = (dataArray[dataIndex] / 255) * canvas.height;
      overallVolume += dataArray[dataIndex];

      const hue = (i / barCount) * 360;
      const lightness = 50 + (barHeight / canvas.height) * 25;
      
      ctx.fillStyle = `hsl(${hue}, 100%, ${lightness}%)`;
      ctx.fillRect(i * barWidth, canvas.height - barHeight, barWidth, barHeight);
    }

    const averageVolume = overallVolume / barCount;
    setIsSpeaking(averageVolume > 10); // Adjust this threshold for speaking detection

    animationFrameIdRef.current = requestAnimationFrame(draw);
  }, []);

  const setupAudio = useCallback(async () => {
    if (isInitialized || (audioContextRef.current && audioContextRef.current.state === 'running')) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;

      setIsInitialized(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      alert('Microphone access denied. Please allow microphone access in your browser settings.');
    }
  }, [isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      animationFrameIdRef.current = requestAnimationFrame(draw);
    }

    // Cleanup function to be called on component unmount
    return () => {
      cancelAnimationFrame(animationFrameIdRef.current);
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [isInitialized, draw]);

  const containerStyle = {
    width: '100px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #333',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    padding: '0 8px',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const micContainerStyle = {
    width: '24px',
    height: '24px',
    marginRight: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  return (
    <div style={containerStyle} onClick={setupAudio} title="Click to activate microphone">
      <div style={micContainerStyle}>
        <MicrophoneIcon isSpeaking={isSpeaking} />
      </div>
      {isInitialized ? (
        <canvas ref={canvasRef} width="60" height="36"></canvas>
      ) : (
        <div style={{ color: '#666', fontSize: '10px', lineHeight: '1.2' }}>
          Click to
          <br />
          start
        </div>
      )}
    </div>
  );
};

export default VoiceVisualizer;